import { trackError } from '@iheartjane/tracking';
import { lazy } from 'react';
import type { ComponentType, LazyExoticComponent } from 'react';

/**
 * Custom lazy load component for retrying failed requests up to 3 times.
 * @see https://goenning.net/2018/11/16/how-to-retry-dynamic-import-with-react-lazy/
 */

type LoadComponent = Parameters<typeof lazy>[0];

export const retry = (
  fn: LoadComponent,
  retriesLeft = 3,
  timeout = 1000
): ReturnType<LoadComponent> => {
  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch((error: Error) => {
        setTimeout(() => {
          if (retriesLeft === 1) {
            trackError(error, { message: 'Error lazy loading page' });
            reject(error);
            return;
          }

          retry(fn, retriesLeft - 1, timeout).then(resolve, reject);
        }, timeout);
      });
  });
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyComponent = ComponentType<any>;

export function load<T extends AnyComponent>(
  fn: () => Promise<{ default: T }>
): LazyExoticComponent<T>;
export function load<M extends Record<K, AnyComponent>, K extends string>(
  fn: () => Promise<M>,
  exportName: K
): LazyExoticComponent<M[K]>;
export function load(
  fn: () => Promise<Record<string, AnyComponent>>,
  exportName = 'default'
) {
  return lazy(() =>
    retry(() => fn().then((module) => ({ default: module[exportName] })))
  );
}
